define('adminQrCodeGeneratorView',[
  'module',
  'underscore',
  'jquery',
  'marionette',
  'qrcodeGenerator',
  'template!adminQrCodeGeneratorView'
], function (
  Module,
  _,
  $,
  Marionette,
  qrcode,
  Tpl
) {
  'use strict';

  var CSS_DPI = 96,
    PNG_DPI = 300,
    MM_PER_INCH = 25.4,
    // Safari refuses canvases above 16.7 Mpx.
    MAX_PNG_PX = 4096,
    MAX_MM = 1000,
    DEFAULT_SIZE = { px: 512, mm: 50 };

  // Byte mode reads the text as UTF-8 (the library default is ISO-8859-1).
  qrcode.stringToBytes = qrcode.stringToBytesFuncs['UTF-8'];

  // Dark modules as horizontal runs, in module units, quiet zone included.
  function buildCode(text, level, margin) {
    var qr = qrcode(0, level);
    qr.addData(text, 'Byte');
    qr.make();
    var count = qr.getModuleCount(),
      runs = [], r, c, start;
    for (r = 0; r < count; r++) {
      start = -1;
      for (c = 0; c <= count; c++) {
        if (c < count && qr.isDark(r, c)) {
          if (start < 0) { start = c; }
        } else if (start >= 0) {
          runs.push({ x: start + margin, y: r + margin, w: c - start });
          start = -1;
        }
      }
    }
    return { count: count, modules: count + 2 * margin, runs: runs };
  }

  function svgPath(code) {
    return _.map(code.runs, function (run) {
      return 'M' + run.x + ' ' + run.y + 'h' + run.w + 'v1h-' + run.w + 'z';
    }).join('');
  }

  function toSvg(code, width) {
    var n = code.modules;
    return '<svg xmlns="http://www.w3.org/2000/svg" width="' + width + '" height="' + width + '"' +
      ' viewBox="0 0 ' + n + ' ' + n + '" shape-rendering="crispEdges">' +
      '<rect width="' + n + '" height="' + n + '" fill="#fff"/>' +
      '<path d="' + svgPath(code) + '" fill="#000"/></svg>';
  }

  function toPngBlob(code, px) {
    var deferred = $.Deferred(),
      canvas = document.createElement('canvas'),
      ctx = canvas.getContext('2d'),
      // Rounded edges: no half-covered pixel between two modules.
      edge = function (i) { return Math.round(i * px / code.modules); };
    canvas.width = px;
    canvas.height = px;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, px, px);
    ctx.fillStyle = '#000';
    _.each(code.runs, function (run) {
      var x = edge(run.x), y = edge(run.y);
      ctx.fillRect(x, y, edge(run.x + run.w) - x, edge(run.y + 1) - y);
    });
    canvas.toBlob(function (blob) { deferred.resolve(blob); }, 'image/png');
    return deferred.promise();
  }

  // pdf-lib is loaded globally (index.html). One filled path, so no hairline
  // shows between neighbouring modules in PDF viewers.
  function toPdfBlob(code, points) {
    var PDFLib = window.PDFLib;
    return PDFLib.PDFDocument.create().then(function (doc) {
      var page = doc.addPage([points, points]);
      page.drawRectangle({ x: 0, y: 0, width: points, height: points, color: PDFLib.rgb(1, 1, 1) });
      page.drawSvgPath(svgPath(code), {
        x: 0,
        y: points,
        scale: points / code.modules,
        color: PDFLib.rgb(0, 0, 0)
      });
      return doc.save();
    }).then(function (bytes) {
      return new Blob([bytes], { type: 'application/pdf' });
    });
  }

  function download(blob, filename) {
    var url = URL.createObjectURL(blob),
      link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  // Admin → QR Code generator: a text / URL to a QR code, previewed live and
  // downloaded as PNG, SVG or PDF. Everything happens in the browser.
  Module.exports = Marionette.ItemView.extend({
    template: Tpl,
    className: 'admin-qrcode-generator-view',

    ui: {
      text: '.js-text',
      size: '.js-size',
      unit: '.js-unit',
      level: '.js-level',
      margin: '.js-margin',
      hint: '.js-hint',
      preview: '.js-preview',
      downloads: '.js-download'
    },

    events: {
      'input @ui.text': 'scheduleRender',
      'input @ui.size': 'scheduleRender',
      'input @ui.margin': 'scheduleRender',
      'change @ui.level': 'renderCode',
      'change @ui.unit': 'onUnitChange',
      'click @ui.downloads': 'onDownload'
    },

    initialize: function () {
      this.scheduleRender = _.debounce(this.renderCode, 150);
    },

    serializeData: function () {
      return {
        size: DEFAULT_SIZE.px,
        levels: [
          { value: 'L', label: _.i18n('admin.qrCodeGenerator.levelL', null, 'Low (7%)') },
          { value: 'M', label: _.i18n('admin.qrCodeGenerator.levelM', null, 'Medium (15%)') },
          { value: 'Q', label: _.i18n('admin.qrCodeGenerator.levelQ', null, 'Quartile (25%)') },
          { value: 'H', label: _.i18n('admin.qrCodeGenerator.levelH', null, 'High (30%)') }
        ],
        labels: {
          text: _.i18n('admin.qrCodeGenerator.text', null, 'Text or URL'),
          textPlaceholder: _.i18n('admin.qrCodeGenerator.textPlaceholder', null, 'https://www.example.com'),
          size: _.i18n('admin.qrCodeGenerator.size', null, 'Size'),
          level: _.i18n('admin.qrCodeGenerator.level', null, 'Error correction'),
          margin: _.i18n('admin.qrCodeGenerator.margin', null, 'Margin (modules)'),
          empty: _.i18n('admin.qrCodeGenerator.empty', null, 'Type a text or a URL to see its QR code')
        }
      };
    },

    onShow: function () {
      this.ui.text.focus();
    },

    onDestroy: function () {
      this.scheduleRender.cancel();
    },

    onUnitChange: function () {
      this.ui.size.val(DEFAULT_SIZE[this.ui.unit.val()]);
      this.renderCode();
    },

    // Size of the whole image (quiet zone included) in each output format.
    readSize: function () {
      var unit = this.ui.unit.val(),
        value = parseFloat(this.ui.size.val()),
        max = unit === 'mm' ? MAX_MM : MAX_PNG_PX;
      if (isNaN(value) || value <= 0 || value > max) {
        return { error: _.i18n('admin.qrCodeGenerator.sizeRange', null, 'The size must be between 1 and') +
          ' ' + max + ' ' + unit + '.' };
      }
      if (unit === 'mm') {
        return {
          unit: unit,
          value: value,
          png: Math.min(MAX_PNG_PX, Math.round(value / MM_PER_INCH * PNG_DPI)),
          points: value / MM_PER_INCH * 72
        };
      }
      value = Math.round(value);
      return { unit: unit, value: value, png: value, points: value / CSS_DPI * 72 };
    },

    renderCode: function () {
      if (this.isDestroyed) { return; }
      var text = this.ui.text.val(),
        margin = Math.max(0, Math.min(20, parseInt(this.ui.margin.val(), 10) || 0)),
        size = this.readSize(),
        hint;
      this.code = null;
      this.size = null;
      this.ui.hint.removeClass('bad warn').empty();
      this.ui.downloads.prop('disabled', true);
      if (!text) {
        this.ui.preview.html($('<span class="qr-empty"/>').text(this.serializeData().labels.empty));
        return;
      }
      try {
        this.code = buildCode(text, this.ui.level.val(), margin);
      } catch (e) {
        this.ui.preview.empty();
        this.ui.hint.addClass('bad').text(_.i18n('admin.qrCodeGenerator.tooLong', null,
          'This text is too long for a QR code, shorten it or lower the error correction.'));
        return;
      }
      this.ui.preview.html(toSvg(this.code, '100%'));
      if (size.error) {
        this.ui.hint.addClass('bad').text(size.error);
        return;
      }
      this.size = size;
      this.ui.downloads.prop('disabled', false);

      var perModule = size.value / this.code.modules;
      hint = this.code.count + ' × ' + this.code.count + ' ' +
        _.i18n('admin.qrCodeGenerator.modules', null, 'modules') + ' · 1 ' +
        _.i18n('admin.qrCodeGenerator.module', null, 'module') + ' = ' +
        (size.unit === 'mm' ? perModule.toFixed(2) : perModule.toFixed(1)) + ' ' + size.unit;
      if (size.png < this.code.modules * 2) {
        this.ui.hint.addClass('warn');
        hint += ' · ' + _.i18n('admin.qrCodeGenerator.tooSmall', null, 'too small to be scanned reliably');
      }
      this.ui.hint.text(hint);
    },

    onDownload: function (e) {
      var code = this.code,
        size = this.size,
        format = $(e.currentTarget).data('format'),
        filename;
      if (!code || !size) { return; }
      filename = 'qrcode-' + size.value + size.unit + '.' + format;
      if (format === 'svg') {
        download(new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n' + toSvg(code, size.value + size.unit)],
          { type: 'image/svg+xml' }), filename);
      } else if (format === 'png') {
        toPngBlob(code, size.png).done(function (blob) { download(blob, filename); });
      } else {
        toPdfBlob(code, size.points).then(function (blob) { download(blob, filename); });
      }
    }
  });
});

